import { Select, Tag, message as antdMessage } from "antd";
import { useEffect, useState } from "react";

type TagRenderProps = Parameters<NonNullable<React.ComponentProps<typeof Select>["tagRender"]>>[0];
import { listAllCaseTags, setCaseTags } from "../api/cases";
import { tagColor } from "../utils/tagColor";

type Props = {
  caseId: string;
  value: string[];
  onChange: (tags: string[]) => void;
};

/**
 * 在 CaseDetailPage 上行内编辑该 case 的 tag。每个 tag 用 hash 派生稳定颜色,
 * autocomplete 选项来自该用户所有 case 上历史出现过的 tag, 用户也能直接输入
 * 新词回车,会自动新建并保存。
 */
export function CaseTagsEditor({ caseId, value, onChange }: Props) {
  const [options, setOptions] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    void listAllCaseTags()
      .then((d) => setOptions(d.tags))
      .catch(() => setOptions([]));
  }, []);

  const commit = async (next: string[]) => {
    // 防止重复(忽略大小写)
    const seen = new Set<string>();
    const dedup: string[] = [];
    for (const t of next) {
      const k = t.trim();
      if (!k) continue;
      const lower = k.toLowerCase();
      if (seen.has(lower)) continue;
      seen.add(lower);
      dedup.push(k);
    }
    onChange(dedup);
    setSaving(true);
    try {
      const res = await setCaseTags(caseId, dedup);
      onChange(res.tags);
      // 把新词追加进 autocomplete pool
      setOptions((prev) => {
        const merged = new Set([...prev, ...res.tags]);
        return [...merged].sort();
      });
    } catch {
      antdMessage.error("保存标签失败");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Select
      mode="tags"
      value={value}
      onChange={commit}
      loading={saving}
      placeholder="添加标签…回车新增"
      tokenSeparators={[",", "，"]}
      style={{ width: "100%", minWidth: 240 }}
      maxTagCount={50}
      tagRender={renderColoredTag}
      options={options.map((t) => ({ value: t, label: <ColorSwatch text={t} /> }))}
      filterOption={(input, opt) =>
        (opt?.value as string).toLowerCase().includes(input.toLowerCase())
      }
    />
  );
}

function renderColoredTag(props: TagRenderProps) {
  const { label, value, closable, onClose } = props;
  const text = String(value ?? label ?? "");
  const c = tagColor(text);
  return (
    <Tag
      closable={closable}
      onClose={onClose}
      style={{
        background: c.bg,
        borderColor: c.border,
        color: c.fg,
        marginInlineEnd: 4,
        fontWeight: 500,
      }}
    >
      {text}
    </Tag>
  );
}

/** Autocomplete 下拉里展示的样式预览 */
function ColorSwatch({ text }: { text: string }) {
  const c = tagColor(text);
  return (
    <span
      style={{
        display: "inline-block",
        padding: "1px 8px",
        borderRadius: 999,
        background: c.bg,
        border: `1px solid ${c.border}`,
        color: c.fg,
        fontSize: 13,
        fontWeight: 500,
      }}
    >
      {text}
    </span>
  );
}

/** 只读用版本(列表页), 不编辑 */
export function CaseTagChip({ text }: { text: string }) {
  const c = tagColor(text);
  return (
    <Tag
      style={{
        background: c.bg,
        borderColor: c.border,
        color: c.fg,
        marginInlineEnd: 4,
        fontWeight: 500,
      }}
    >
      {text}
    </Tag>
  );
}
